import React, { useRef } from "react";
import type { Attachment } from "../types/gridTypes";
import {
  IconAttachment,
  IconDownload,
  IconTrash,
  IconPlus,
  IconEye,
} from "./icons";

interface Props {
  attachments: Attachment[];
  onUpload?: (files: FileList) => void;
  onDelete?: (attachmentId: string) => void;
}

export const SidebarAttachments: React.FC<Props> = ({
  attachments,
  onUpload,
  onDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload?.(e.target.files);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = (e: React.MouseEvent, att: Attachment) => {
    e.stopPropagation();
    if (!att.url) return;

    if (att.url === "#") {
      // Handle dummy content for development/testing
      const dummyContent = `This is dummy content for ${att.name}\nSize: ${att.size} bytes`;
      const blob = new Blob([dummyContent], { type: att.type || "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Handle real Azure Blob Storage URLs
      const a = document.createElement("a");
      a.href = att.url;
      a.download = att.name;
      a.target = "_blank"; // Open in new tab as fallback
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handlePreview = (e: React.MouseEvent, att: Attachment) => {
    e.stopPropagation();
    if (!att.url || att.url === "#") return;

    // Open in new tab for preview
    window.open(att.url, "_blank", "noopener,noreferrer");
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <div className="sidebar-panel flex flex-col h-full">
      {onUpload && (
        <div className="sidebar-panel-header p-4 border-b">
          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="sidebar-upload-btn w-full flex items-center justify-center gap-2 border px-4 py-2 rounded-md text-sm font-medium transition-all shadow-sm"
          >
            <IconPlus className="w-4 h-4" />
            <span>Upload Files</span>
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2 flex-1 p-4 overflow-y-auto">
        {attachments.length === 0 && (
          <div className="sidebar-empty-state flex flex-col items-center justify-center py-12 italic text-center rounded-lg border border-dashed">
            <IconAttachment className="w-8 h-8 mb-2" />
            <p className="text-sm font-medium">No attachments</p>
            <p className="text-xs mt-1">Drag files here or click Upload</p>
          </div>
        )}
        {attachments.map((att) => (
          <div
            key={att.id}
            className="sidebar-item group flex items-center gap-3 p-3 rounded-lg border border-transparent transition-all cursor-default"
          >
            {/* Icon */}
            <div className="sidebar-item-icon w-9 h-9 flex items-center justify-center rounded-lg shrink-0 text-[10px] font-bold uppercase tracking-wide border">
              {att.type
                ? att.type.split("/")[1]?.substring(0, 3) || "DOC"
                : "DOC"}
            </div>

            {/* Meta */}
            <div className="flex-1 min-w-0">
              <div
                className="sidebar-item-title text-sm font-bold truncate"
                title={att.name}
              >
                {att.name}
              </div>
              <div className="sidebar-item-meta flex items-center gap-1.5 text-xs mt-0.5">
                <span>{formatSize(att.size)}</span>
                <span className="sidebar-dot w-0.5 h-0.5 rounded-full"></span>
                <span>{att.uploadedBy || "User"}</span>
                <span className="sidebar-dot w-0.5 h-0.5 rounded-full"></span>
                <span>{new Date(att.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => handlePreview(e, att)}
                className="sidebar-action-btn p-1.5 rounded-md transition-colors"
                title="Preview"
              >
                <IconEye className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => handleDownload(e, att)}
                className="sidebar-action-btn p-1.5 rounded-md transition-colors"
                title="Download"
              >
                <IconDownload className="w-4 h-4" />
              </button>
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(att.id);
                  }}
                  className="sidebar-action-btn-danger p-1.5 rounded-md transition-colors"
                  title="Delete"
                >
                  <IconTrash className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
